import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axiosInstance from '../utils/axios';
import {
  ArrowLeftIcon,
  SignalIcon,
  SignalSlashIcon,
  ServerIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  CpuChipIcon,
  WifiIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BoltIcon,
  Cog6ToothIcon,
  WrenchScrewdriverIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  CommandLineIcon,
  ChartBarIcon,
  BellAlertIcon,
  BeakerIcon,
  CloudArrowUpIcon,
  ArrowPathIcon,
  CircleStackIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { format, formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../config/permissions';
import { isDM510 } from '../utils/modelLabel';
import DeviceTerminal from '../components/DeviceTerminal';
import DeviceHistory from '../components/DeviceHistory';
import Beta2History from '../components/Beta2History';
import CalibrationHistory from '../components/CalibrationHistory';
import AlarmHistory from './AlarmHistory';
import SessionActivity from '../components/SessionActivity';
import DeviceSubscriptions from '../components/DeviceSubscriptions';
import DeviceSoftwareManager from '../components/DeviceSoftwareManager';
import DatabaseHealthManager from '../components/DatabaseHealthManager';

// Helper function to format location data
const formatLocation = (location) => {
  if (!location) return 'N/A';

  if (typeof location === 'string') return location;

  if (location.formatted) return location.formatted;

  if (typeof location === 'object') {
    const parts = [
      location.city,
      location.region,
      location.country
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : 'N/A';
  }

  return 'N/A';
};

const InfoItem = ({ icon: Icon, label, value, className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <Icon className="w-5 h-5 text-gray-400" />
    <div>
      <p className="text-sm text-gray-600">{label}</p>
      {typeof value === 'string' || typeof value === 'number' || value === null || value === undefined ? (
        <p className="font-medium text-gray-900">{value || 'N/A'}</p>
      ) : (
        <div className="font-medium text-gray-900">{value}</div>
      )}
    </div>
  </div>
);


const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>
        {isOpen ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-gray-200"
          >
            <div className="p-6">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Render topic data in a structured format
// renderTopicData function removed - WebSocket functionality not needed for employees
const renderTopicData = (topic, data) => {
  if (!data) return <p className="text-gray-500 text-sm">No data available</p>;

  // Handle different topic types
  if (topic === 'home') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 uppercase mb-1">{key.replace(/_/g, ' ')}</p>
            <p className="text-lg font-semibold text-gray-900">
              {typeof value === 'object' ? JSON.stringify(value) : value}
            </p>
          </div>
        ))}
      </div>
    );
  }

  if (topic === 'settings') {
    return (
      <div className="space-y-3">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">{key.replace(/_/g, ' ')}</span>
            <span className="text-sm font-medium text-gray-900">
              {typeof value === 'boolean' ? (value ? 'Enabled' : 'Disabled') : value}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (topic === 'diagnostic') {
    return (
      <div className="space-y-4">
        {Object.entries(data).map(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            return (
              <div key={key} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">{key.replace(/_/g, ' ')}</h4>
                <div className="space-y-1">
                  {Object.entries(value).map(([subKey, subValue]) => (
                    <div key={subKey} className="flex justify-between text-sm">
                      <span className="text-gray-600">{subKey.replace(/_/g, ' ')}</span>
                      <span className="text-gray-900">{subValue}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          return (
            <div key={key} className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{key.replace(/_/g, ' ')}</span>
              <span className="text-sm font-medium text-gray-900">{value}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // Default JSON display for unknown topics
  return (
    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
      <pre className="text-green-400 text-xs font-mono">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
};

const DeviceDetail = () => {
  const { serial } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [showRebootDialog, setShowRebootDialog] = useState(false);
  const [rebootCountdown, setRebootCountdown] = useState(null);
  const [rebootError, setRebootError] = useState(null);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [editingSoldBy, setEditingSoldBy] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [soldDate, setSoldDate] = useState(null);
  const [showSoldDatePicker, setShowSoldDatePicker] = useState(false);
  const [editingDryerInfo, setEditingDryerInfo] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [dryerModelValue, setDryerModelValue] = useState('');
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [showOwnersDropdown, setShowOwnersDropdown] = useState(false);
  // Serial numbers state
  const [editingSerials, setEditingSerials] = useState(false);
  const [hardwareSerialValue, setHardwareSerialValue] = useState('');
  const [smartBoardSerialValue, setSmartBoardSerialValue] = useState('');
  const [savingSerials, setSavingSerials] = useState(false);
  const [serialsError, setSerialsError] = useState(null);
  // Device SSH password state (developer only)
  const [devicePw, setDevicePw] = useState(null);
  const [showDevicePw, setShowDevicePw] = useState(false);
  const [devicePwBusy, setDevicePwBusy] = useState(false);
  const [devicePwMessage, setDevicePwMessage] = useState(null);
  // Hardware replacement state (developer only)
  const [showHardwareDialog, setShowHardwareDialog] = useState(false);
  const [hwCandidates, setHwCandidates] = useState([]);
  const [hwSourceSerial, setHwSourceSerial] = useState('');
  const [hwBusy, setHwBusy] = useState(false);
  const [hwError, setHwError] = useState(null);
  const [hwSuccess, setHwSuccess] = useState(null);
  // Reset for customer delivery state (developer only)
  const [customerReset, setCustomerReset] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetConfirmSerial, setResetConfirmSerial] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [resetSent, setResetSent] = useState(false);
  // Demo mark + registration password reset (admin and up)
  const [demoBusy, setDemoBusy] = useState(false);
  const [resetPwBusy, setResetPwBusy] = useState(false);
  const dropdownRef = useRef(null);
  const ownersDropdownRef = useRef(null);
  const hardwareSerialInputRef = useRef(null);

  // Click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowActionsDropdown(false);
      }
      if (ownersDropdownRef.current && !ownersDropdownRef.current.contains(event.target)) {
        setShowOwnersDropdown(false);
      }
    };

    if (showActionsDropdown || showOwnersDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showActionsDropdown, showOwnersDropdown]);

  // Fetch device details function
  const fetchDevice = async () => {
    try {
      const response = await axiosInstance.get(`/device-details/${serial}`);
      const data = response.data;
      setDevice(data.device);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch device details on mount
  useEffect(() => {
    fetchDevice();
  }, [serial]);

  // ----- Device SSH password (developer only) -----
  const canSeeDevicePassword = hasRole(user?.role, 'developer');

  const fetchDevicePassword = async () => {
    try {
      const response = await axiosInstance.get(`/device-details/${serial}/device-password`);
      setDevicePw(response.data);
    } catch (err) {
      setDevicePw(null);
    }
  };

  useEffect(() => {
    if (canSeeDevicePassword && serial) {
      fetchDevicePassword();
    }
  }, [serial, canSeeDevicePassword]);

  const handleAssignDevicePassword = async () => {
    setDevicePwBusy(true);
    setDevicePwMessage(null);
    try {
      const response = await axiosInstance.post(`/device-details/${serial}/device-password`);
      setDevicePwMessage(response.data.message);
      await fetchDevicePassword();
      // The agent applies and confirms within seconds — pick up the promoted
      // status without asking for a manual refresh.
      setTimeout(fetchDevicePassword, 5000);
    } catch (err) {
      setDevicePwMessage(err.response?.data?.message || 'Failed to assign password');
    } finally {
      setDevicePwBusy(false);
    }
  };

  // ----- Reset for customer delivery (developer only) -----
  const fetchCustomerReset = async () => {
    try {
      const response = await axiosInstance.get(`/device-details/${serial}/customer-reset`);
      setCustomerReset(response.data);
    } catch (err) {
      setCustomerReset(null);
    }
  };
  useEffect(() => {
    if (canSeeDevicePassword && serial) {
      fetchCustomerReset();
    }
  }, [serial, canSeeDevicePassword]);
  // The box answers within a few seconds; keep asking until it does.
  useEffect(() => {
    if (customerReset?.status !== 'pending') return undefined;
    const timer = setInterval(fetchCustomerReset, 3000);
    return () => clearInterval(timer);
  }, [customerReset?.status]);
  const openResetDialog = () => {
    setResetConfirmSerial('');
    setResetError(null);
    setResetSent(false);
    setShowResetDialog(true);
  };
  const handleCustomerReset = async () => {
    if (resetConfirmSerial !== serial) return;
    setResetBusy(true);
    setResetError(null);
    try {
      await axiosInstance.post(`/device-details/${serial}/customer-reset`, { confirmSerial: resetConfirmSerial });
      setResetSent(true);
      await fetchCustomerReset();
    } catch (err) {
      setResetError(err.response?.data?.message || 'Failed to request the reset');
    } finally {
      setResetBusy(false);
    }
  };

  // ----- DM510 replacement box (developer only): the serial belongs to the panel, so the new box registers under it -----
  const [replacementBusy, setReplacementBusy] = useState(false);
  const replacementOpenUntil = device?.replacement?.allowedUntil && new Date(device.replacement.allowedUntil) > new Date()
    ? new Date(device.replacement.allowedUntil) : null;
  const handleAllowReplacement = async () => {
    const closing = !!replacementOpenUntil;
    const question = closing
      ? `Close the replacement window of ${serial}? A new box could then no longer register under this serial.`
      : `Allow a replacement box to register as ${serial} for the next seven days? Owners, history and alarms stay with the serial; the old box can never come back afterwards.`;
    if (!window.confirm(question)) return;
    setReplacementBusy(true);
    try {
      if (closing) {
        await axiosInstance.delete(`/device-details/${serial}/allow-replacement`);
      } else {
        await axiosInstance.post(`/device-details/${serial}/allow-replacement`);
      }
      await fetchDevice();
    } catch (err) {
      window.alert(err.response?.data?.message || 'The replacement window could not be changed');
    } finally {
      setReplacementBusy(false);
    }
  };

  // ----- Hardware replacement (developer only) -----
  const openHardwareDialog = async () => {
    setShowHardwareDialog(true);
    setHwError(null);
    setHwSuccess(null);
    setHwSourceSerial('');
    try {
      const response = await axiosInstance.get('/device-details/');
      const list = (response.data.devices || []).filter((d) => d.serial !== serial);
      setHwCandidates(list);
    } catch (err) {
      setHwError('Could not load the device list');
      setHwCandidates([]);
    }
  };

  const handleHardwareReplaced = async () => {
    if (!hwSourceSerial) return;
    setHwBusy(true);
    setHwError(null);
    try {
      const response = await axiosInstance.post(`/device-details/${serial}/hardware-replaced`, {
        sourceSerial: hwSourceSerial,
      });
      setHwSuccess(response.data.message);
      await fetchDevice();
      await fetchDevicePassword();
    } catch (err) {
      setHwError(err.response?.data?.message || 'Hardware transfer failed');
    } finally {
      setHwBusy(false);
    }
  };

  // SSE connection for real-time device status
  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem('token');
    const sseUrl = import.meta.env.VITE_SSE_BASE_URL || 'http://localhost:3012/api/sse';
    const eventSource = new EventSource(`${sseUrl}/events?token=${token}`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Handle device connection/disconnection events
        if ((data.type === 'device_connected' || data.type === 'device_disconnected') && data.serial === serial) {
          const isOnline = data.type === 'device_connected';
          setDevice(prev => ({
            ...prev,
            isOnline,
            lastSeen: data.timestamp || new Date().toISOString(),
            connectedAt: isOnline ? data.timestamp : prev?.connectedAt,
            sessionId: data.sessionId || prev?.sessionId
          }));
          
        }
      } catch (err) {
        console.error('Error parsing SSE data:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('SSE error:', err);
    };

    return () => {
      eventSource.close();
    };
  }, [user, serial]);

  // Handle disconnect/refresh connection
  const handleDisconnect = async () => {
    setDisconnectLoading(true);
    setShowActionsDropdown(false);
    
    try {
      const response = await axiosInstance.post(`/device-details/${serial}/disconnect`);
      
      if (response.data.success) {
        // Show success notification (could use a toast library here)
        console.log('Device disconnected successfully');
        
        // The device status will update automatically via WebSocket/polling
        // Optionally refresh device data after a short delay
        setTimeout(() => {
          fetchDevice();
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to disconnect device:', err);
      // You could show an error toast here
    } finally {
      setDisconnectLoading(false);
    }
  };

  // Fetch sellers for dropdown
  const fetchSellers = async () => {
    try {
      const response = await axiosInstance.get('/sellers');
      setSellers(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch sellers:', error);
      setSellers([]);
    }
  };

  // Handle updating sold by information
  const handleUpdateSoldBy = async () => {
    try {
      const updateData = {
        soldBy: selectedSeller
      };

      // Only include soldDate if the checkbox is checked
      if (showSoldDatePicker) {
        // If soldDate is a string from date input (YYYY-MM-DD), convert to proper date
        if (soldDate) {
          // Split the date string to avoid timezone issues
          const [year, month, day] = soldDate.split('-').map(Number);
          const now = new Date();
          // Create date with local timezone using individual components
          const selectedDate = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());
          updateData.soldDate = selectedDate.toISOString();
        } else {
          // Use current date and time if no date selected
          updateData.soldDate = new Date().toISOString();
        }
      } else if (!showSoldDatePicker && device.soldDate) {
        // Clear soldDate if unchecked and it existed before
        updateData.soldDate = null;
      }

      const response = await axiosInstance.put(`/device-details/${serial}/sold-by`, updateData);

      if (response.data.success) {
        // Fetch the updated device to get the populated soldBy data
        const deviceResponse = await axiosInstance.get(`/device-details/${serial}`);
        if (deviceResponse.data.success) {
          setDevice(deviceResponse.data.device);
        }

        setEditingSoldBy(false);
        setSelectedSeller(null);
        setSoldDate(null);
        setShowSoldDatePicker(false);
      }
    } catch (error) {
      console.error('Failed to update sold by:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update sold information. Please try again.';
      alert(errorMessage);
    }
  };

  // Handle updating dryer manufacturer / model
  const handleUpdateDryerInfo = async () => {
    try {
      const response = await axiosInstance.put(`/device-details/${serial}/dryer-info`, {
        dryerManufacturer: selectedManufacturer || null,
        dryerModel: dryerModelValue.trim() || null
      });

      if (response.data.success) {
        // Fetch the updated device to get the populated manufacturer data
        const deviceResponse = await axiosInstance.get(`/device-details/${serial}`);
        if (deviceResponse.data.success) {
          setDevice(deviceResponse.data.device);
        }

        setEditingDryerInfo(false);
        setSelectedManufacturer('');
        setDryerModelValue('');
      }
    } catch (error) {
      console.error('Failed to update dryer info:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update dryer information. Please try again.';
      alert(errorMessage);
    }
  };

  // Handle reboot command
  const handleReboot = async () => {
    setRebootError(null);
    setRebootCountdown(5); // 5 second countdown
    
    try {
      const response = await axiosInstance.post(`/device-details/${serial}/reboot`, {
        delay: 5
      });
      
      const data = response.data;
      
      // Start countdown
      let count = 5;
      const countdownInterval = setInterval(() => {
        count--;
        setRebootCountdown(count);
        if (count <= 0) {
          clearInterval(countdownInterval);
          setShowRebootDialog(false);
          setRebootCountdown(null);
          // Device will be offline soon, update status
          setTimeout(() => {
            setDevice(prev => ({ ...prev, isOnline: false }));
          }, 2000);
        }
      }, 1000);
      
    } catch (err) {
      setRebootError(err.message);
      setRebootCountdown(null);
    }
  };

  const isDeveloper = hasRole(user?.role, 'developer');
  // A DM510 is a panel we read over Modbus plus a small box: no model internals,
  // no calibration history, no Postgres on the box. Those sections stay hidden.
  const is510 = isDM510(device?.model);
  const isAdmin = hasRole(user?.role, 'admin');

  // Demo unit: on loan, not sold. Nobody pays for a demo box and it never
  // counts toward the single/multi price.
  const handleToggleDemo = async () => {
    setDemoBusy(true);
    try {
      await axiosInstance.put(`/device-details/${serial}/demo`, { demo: !device.demo });
      await fetchDevice();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update the demo mark. Please try again.');
    } finally {
      setDemoBusy(false);
    }
  };

  // A box that changes hands needs a fresh registration password before the
  // phone app will let the next owner register it.
  const handleResetRegisterPassword = async () => {
    if (!window.confirm(`Issue a new registration password for ${serial}? The old one stops working immediately.`)) {
      return;
    }
    setResetPwBusy(true);
    try {
      const response = await axiosInstance.post(`/device-details/${serial}/reset-register-password`);
      await fetchDevice();
      setShowPassword(true);
      const holders = response.data.device?.activeHolders || 0;
      if (holders > 0) {
        alert(`New password issued. ${holders} account${holders === 1 ? ' still holds' : 's still hold'} this device - remove ${holders === 1 ? 'it' : 'them'} from the user page, or the phone app will refuse the new registration.`);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reset the registration password. Please try again.');
    } finally {
      setResetPwBusy(false);
    }
  };

  // Handle serial numbers edit
  const canEditSerials = () => {
    // Can edit if fields are empty OR user is a developer
    const hwEmpty = !device.hardwareSerial || device.hardwareSerial.trim() === '';
    const sbEmpty = !device.smartBoardSerial || device.smartBoardSerial.trim() === '';
    return isDeveloper || hwEmpty || sbEmpty;
  };

  const handleSerialsEdit = () => {
    setHardwareSerialValue(device.hardwareSerial || '');
    setSmartBoardSerialValue(device.smartBoardSerial || '');
    setSerialsError(null);
    setEditingSerials(true);
    setTimeout(() => hardwareSerialInputRef.current?.focus(), 100);
  };

  const handleSerialsSave = async () => {
    setSavingSerials(true);
    setSerialsError(null);
    try {
      const updateData = {};
      // Only include fields that changed or are being set for the first time
      if (hardwareSerialValue.trim() !== (device.hardwareSerial || '')) {
        updateData.hardwareSerial = hardwareSerialValue.trim() || null;
      }
      if (smartBoardSerialValue.trim() !== (device.smartBoardSerial || '')) {
        updateData.smartBoardSerial = smartBoardSerialValue.trim() || null;
      }

      if (Object.keys(updateData).length === 0) {
        setEditingSerials(false);
        return;
      }

      const response = await axiosInstance.put(`/device-details/${serial}/serials`, updateData);
      if (response.data.success) {
        setDevice(prev => ({
          ...prev,
          hardwareSerial: response.data.device.hardwareSerial,
          smartBoardSerial: response.data.device.smartBoardSerial
        }));
        setEditingSerials(false);
      }
    } catch (error) {
      console.error('Failed to update serials:', error);
      setSerialsError(error.response?.data?.message || 'Failed to update serial numbers');
    } finally {
      setSavingSerials(false);
    }
  };

  const handleSerialsCancel = () => {
    setEditingSerials(false);
    setHardwareSerialValue('');
    setSmartBoardSerialValue('');
    setSerialsError(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error || 'Device not found'}</p>
        <button
          onClick={() => navigate('/dashboard/devices')}
          className="text-blue-600 hover:text-blue-700"
        >
          Back to devices
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Fixed Device Summary Card */}
      <div className="fixed top-16 left-0 right-0 z-30 px-6 py-3 bg-gray-50 border-b border-gray-200 shadow-sm ml-64">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            {/* Left side - Back button and device info */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard/devices')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
              </button>

              <div className="flex items-center gap-6">
                {/* Serial Number */}
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Serial</p>
                  <p className="font-mono font-semibold text-gray-900 flex items-center gap-2">
                    {device.serial}
                    {device.demo && (
                      <span className="px-2 py-0.5 text-xs font-sans font-semibold tracking-wide rounded-full bg-purple-100 text-purple-700">
                        DEMO
                      </span>
                    )}
                    {device.demo && device.owners?.some((owner) => owner.role === 'demo') && (
                      <span
                        title="On the public demo login: anyone can watch this box from the marketing site"
                        className="px-2 py-0.5 text-xs font-sans font-semibold tracking-wide rounded-full bg-amber-100 text-amber-800">
                        PUBLIC
                      </span>
                    )}
                  </p>
                </div>

                {/* Cabinet Serial - the number the CRM records at shipping,
                    set with the other serials further down the page */}
                <div className="hidden sm:block">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Cabinet Serial</p>
                  <p className="font-mono font-semibold text-gray-900">
                    {device.hardwareSerial || <span className="font-sans font-normal text-gray-400 italic">Not set</span>}
                  </p>
                </div>

                {/* Owner(s) */}
                <div className="hidden md:block relative" ref={ownersDropdownRef}>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                    {device.owners && device.owners.length > 1 ? 'Owners' : 'Owner'}
                  </p>
                  {device.owners && device.owners.length > 0 ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => navigate(`/dashboard/users/${device.owners[0]._id}`)}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {device.owners[0].firstName} {device.owners[0].lastName}
                      </button>
                      {device.owners.length > 1 && (
                        <button
                          onClick={() => setShowOwnersDropdown(!showOwnersDropdown)}
                          className="text-xs text-blue-600 bg-blue-50 hover:bg-blue-100 px-1.5 py-0.5 rounded font-medium transition-colors"
                        >
                          +{device.owners.length - 1}
                        </button>
                      )}

                      {/* Owners Dropdown */}
                      {showOwnersDropdown && device.owners.length > 1 && (
                        <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[200px] z-50">
                          <p className="px-3 py-1 text-xs text-gray-500 font-medium uppercase">All Owners ({device.owners.length})</p>
                          <div className="max-h-48 overflow-y-auto">
                            {device.owners.map((owner) => (
                              <button
                                key={owner._id}
                                onClick={() => {
                                  setShowOwnersDropdown(false);
                                  navigate(`/dashboard/users/${owner._id}`);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors text-left"
                              >
                                <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-medium text-blue-700">
                                    {owner.firstName?.charAt(0).toUpperCase() || 'U'}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {owner.firstName} {owner.lastName}
                                    {owner.isDefault && (
                                      <span className="ml-1 text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Primary</span>
                                    )}
                                  </p>
                                  {owner.farmName && (
                                    <p className="text-xs text-green-600 truncate">{owner.farmName}</p>
                                  )}
                                  <p className="text-xs text-gray-500 truncate">{owner.email}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="font-medium text-gray-400 italic">No owner</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right side - Status and actions */}
            <div className="flex items-center gap-4">
              {/* Connection Status */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                device.isOnline
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-gray-100 border border-gray-200'
              }`}>
                {device.isOnline ? (
                  <>
                    <div className="relative">
                      <span className="w-2.5 h-2.5 bg-green-500 rounded-full block"></span>
                      <span className="w-2.5 h-2.5 bg-green-500 rounded-full absolute top-0 left-0 animate-ping"></span>
                    </div>
                    <span className="font-medium text-green-700 text-sm">Online</span>
                    {device.connectedAt && (
                      <span className="text-xs text-green-600 hidden sm:inline">
                        • {formatDistanceToNow(new Date(device.connectedAt))}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="w-2.5 h-2.5 bg-gray-400 rounded-full block"></span>
                    <span className="font-medium text-gray-600 text-sm">Offline</span>
                    {device.lastSeen && (
                      <span className="text-xs text-gray-500 hidden sm:inline">
                        • {formatDistanceToNow(new Date(device.lastSeen), { addSuffix: true })}
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Hardware replaced - deliberately available offline: the
                  typical trigger is a dead unit */}
              {isDeveloper && !is510 && (
                <button
                  onClick={openHardwareDialog}
                  className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm"
                >
                  <CpuChipIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">Hardware Replaced</span>
                </button>
              )}

              {/* A DM510 replacement box registers under the panel's serial: open the window for it */}
              {isDeveloper && is510 && (
                <button
                  onClick={handleAllowReplacement}
                  disabled={replacementBusy}
                  className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                  title={replacementOpenUntil ? 'Close the window a replacement box could register in' : 'Let a replacement box register under this serial for the next seven days'}
                >
                  <CpuChipIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">{replacementBusy ? 'Sending…' : replacementOpenUntil ? 'Close Replacement Window' : 'Allow Replacement Box'}</span>
                </button>
              )}

              {/* Device Actions - developer tools (terminal, refresh, reboot) */}
              {device.isOnline && isDeveloper && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
                  >
                    <WrenchScrewdriverIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">Actions</span>
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${showActionsDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {showActionsDropdown && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                      <button
                        onClick={() => {
                          setShowTerminal('host');
                          setShowActionsDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <CommandLineIcon className="w-5 h-5 text-green-600" />
                        <span className="text-gray-700">Terminal</span>
                      </button>

                      <button
                        onClick={handleDisconnect}
                        disabled={disconnectLoading}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-yellow-50 transition-colors text-left border-t border-gray-100 disabled:opacity-50"
                      >
                        <ArrowPathIcon className={`w-5 h-5 text-yellow-600 ${disconnectLoading ? 'animate-spin' : ''}`} />
                        <span className="text-gray-700">
                          {disconnectLoading ? 'Disconnecting...' : 'Refresh Connection'}
                        </span>
                      </button>

                      <button
                        onClick={() => {
                          setShowRebootDialog(true);
                          setShowActionsDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors text-left border-t border-gray-100"
                      >
                        <ArrowPathIcon className="w-5 h-5 text-red-600" />
                        <span className="text-red-700 font-medium">Reboot Device</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-20"></div>

      {/* Device Information */}
      <CollapsibleSection
        title="Device Information"
        icon={InformationCircleIcon}
        defaultOpen={false}
      >
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <InfoItem
            icon={CpuChipIcon}
            label="Model"
            value={device.model}
          />
          <InfoItem
            icon={WifiIcon}
            label="IP Address"
            value={device.ip}
          />
          <InfoItem
            icon={MapPinIcon}
            label="Location"
            value={formatLocation(device.location)}
          />
          <InfoItem
            icon={ServerIcon}
            label="Hostname"
            value={device.hostname}
          />
          {is510 && replacementOpenUntil && (
            <InfoItem
              icon={CpuChipIcon}
              label="Replacement Box"
              value={`A new box may register under this serial until ${format(replacementOpenUntil, 'MMMM d, yyyy h:mm a')}`}
            />
          )}

          <InfoItem
            icon={ClockIcon}
            label="Registered"
            value={device.registeredAt ? (
              <div>
                <div className="font-medium text-gray-900">
                  {format(new Date(device.registeredAt), 'MMMM d, yyyy')}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {format(new Date(device.registeredAt), 'h:mm a')} • {formatDistanceToNow(new Date(device.registeredAt), { addSuffix: true })}
                </div>
              </div>
            ) : 'N/A'}
          />
          <InfoItem
            icon={UserIcon}
            label="Created By"
            value={device.createdBy ? `${device.createdBy.firstName} ${device.createdBy.lastName}` : 'N/A'}
          />
          {/* Sold By / Manufactured with Edit Capability */}
          <div className="flex items-start gap-3">
            <UserIcon className="w-5 h-5 text-gray-400 mt-1" />
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-1">
                {device.soldBy?.role === 'inventory' ? 'Manufactured' : 'Sold By'}
              </p>
              {!editingSoldBy ? (
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900">
                    {device.soldBy?.role === 'inventory'
                      ? 'Dryer Master Inc'
                      : device.soldBy?.name || 'Not specified'}
                  </p>
                  <button
                    onClick={() => {
                      setEditingSoldBy(true);
                      fetchSellers();
                      setSelectedSeller(device.soldBy?._id || null);
                      // Format existing sold date for date input
                      if (device.soldDate) {
                        setSoldDate(format(new Date(device.soldDate), 'yyyy-MM-dd'));
                      } else {
                        setSoldDate(null);
                      }
                      setShowSoldDatePicker(!!device.soldDate);
                    }}
                    className="text-blue-600 hover:text-blue-700 text-sm"
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <select
                    value={selectedSeller || ''}
                    onChange={(e) => setSelectedSeller(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a vendor...</option>
                    {sellers.map((seller) => (
                      <option key={seller._id} value={seller._id}>
                        {seller.name} ({seller.role})
                      </option>
                    ))}
                  </select>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="addSoldDate"
                      checked={showSoldDatePicker}
                      onChange={(e) => {
                        setShowSoldDatePicker(e.target.checked);
                        if (!e.target.checked) {
                          setSoldDate(null);
                        }
                      }}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="addSoldDate" className="text-sm text-gray-700">
                      Add sold date
                    </label>
                  </div>

                  {showSoldDatePicker && (
                    <input
                      type="date"
                      value={soldDate || ''}
                      onChange={(e) => setSoldDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={handleUpdateSoldBy}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingSoldBy(false);
                        setSelectedSeller(null);
                        setSoldDate(null);
                        setShowSoldDatePicker(false);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Show sold date if exists */}
              {device.soldDate && !editingSoldBy && (
                <div className="mt-2 pl-8">
                  <p className="text-xs text-gray-500 font-medium">Sold Date</p>
                  <div className="mt-0.5">
                    <div className="text-sm font-medium text-gray-900">
                      {format(new Date(device.soldDate), 'MMMM d, yyyy')}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {format(new Date(device.soldDate), 'h:mm a')} • {formatDistanceToNow(new Date(device.soldDate), { addSuffix: true })}
                    </div>
                  </div>
                </div>
              )}

              {/* Demo unit - on loan, not sold. Nobody pays for a demo box and
                  it never counts toward the single/multi price. */}
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="demoUnit"
                  checked={!!device.demo}
                  disabled={!isAdmin || demoBusy}
                  onChange={handleToggleDemo}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 disabled:opacity-50"
                />
                <label htmlFor="demoUnit" className={`text-sm ${isAdmin ? 'text-gray-700' : 'text-gray-500'}`}>
                  Demo unit
                </label>
                {device.demo && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                    DEMO
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Dryer Manufacturer / Model with Edit Capability */}
          <div className="flex items-start gap-3">
            <WrenchScrewdriverIcon className="w-5 h-5 text-gray-400 mt-1" />
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-1">Dryer</p>
              {!editingDryerInfo ? (
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900">
                    {device.dryerManufacturer?.name
                      ? `${device.dryerManufacturer.name}${device.dryerModel ? ` — ${device.dryerModel}` : ''}`
                      : 'Manufacturer not specified'}
                  </p>
                  <button
                    onClick={() => {
                      setEditingDryerInfo(true);
                      fetchSellers();
                      setSelectedManufacturer(device.dryerManufacturer?._id || '');
                      setDryerModelValue(device.dryerModel || '');
                    }}
                    className="text-blue-600 hover:text-blue-700 text-sm"
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <select
                    value={selectedManufacturer || ''}
                    onChange={(e) => setSelectedManufacturer(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No manufacturer</option>
                    {sellers.map((seller) => (
                      <option key={seller._id} value={seller._id}>
                        {seller.name} ({seller.role})
                      </option>
                    ))}
                  </select>

                  <input
                    type="text"
                    value={dryerModelValue}
                    onChange={(e) => setDryerModelValue(e.target.value)}
                    placeholder="Dryer model"
                    maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />

                  <div className="flex gap-2">
                    <button
                      onClick={handleUpdateDryerInfo}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingDryerInfo(false);
                        setSelectedManufacturer('');
                        setDryerModelValue('');
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Access the owner has granted to outside companies (set in the phone app) */}
          <InfoItem
            icon={KeyIcon}
            label="External Access"
            value={
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  device.externalAccess?.dealer
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  Dealer {device.externalAccess?.dealer ? 'enabled' : 'disabled'}
                </span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  device.externalAccess?.manufacturer
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  Manufacturer {device.externalAccess?.manufacturer ? 'enabled' : 'disabled'}
                </span>
              </div>
            }
          />
          <InfoItem
            icon={CpuChipIcon}
            label="Software Version"
            value={device.softwareVersion || 'N/A'}
          />
          
          {/* Device Owners/Subscribers */}
          {device.owners && device.owners.length > 0 && (
            <div className="flex items-start gap-3">
              <UserIcon className="w-5 h-5 text-gray-400 mt-1" />
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-2">Device Owners</p>
                <div className="space-y-2">
                  {device.owners.map((owner) => (
                    <button
                      key={owner._id}
                      onClick={() => navigate(`/dashboard/users/${owner._id}`)}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors w-full text-left"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-700">
                          {owner.firstName ? owner.firstName.charAt(0).toUpperCase() : 'U'}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {owner.firstName} {owner.lastName}
                          {owner.isDefault && (
                            <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Primary</span>
                          )}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{owner.email}</span>
                          <span className={`px-2 py-0.5 rounded ${
                            owner.role === 'operator' 
                              ? 'bg-purple-100 text-purple-700'
                              : owner.role === 'demo'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {owner.role === 'operator' ? 'Operator' : owner.role === 'demo' ? 'Demo' : 'User'}
                          </span>
                          {owner.farmName && <span>{owner.farmName}</span>}
                        </div>
                      </div>
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Registration Password */}
          <div className="flex items-center gap-3">
            <KeyIcon className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">Registration Password</p>
              <div className="flex items-center gap-2">
                <span className="font-mono font-medium text-gray-900">
                  {showPassword ? device.registerPassword : '••••••••'}
                </span>
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-4 h-4 text-gray-600" />
                  ) : (
                    <EyeIcon className="w-4 h-4 text-gray-600" />
                  )}
                </button>
                {device.passwordUsed !== undefined && (
                  <div className="flex items-center gap-1">
                    {device.passwordUsed ? (
                      <>
                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-600">Used</span>
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="w-4 h-4 text-amber-600" />
                        <span className="text-sm text-amber-600">Not Used</span>
                      </>
                    )}
                  </div>
                )}
                {isAdmin && (
                  <button
                    onClick={handleResetRegisterPassword}
                    disabled={resetPwBusy}
                    title="Issue a new registration password so the device can be registered again"
                    className="ml-2 px-2.5 py-1 text-xs font-medium rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50 transition-colors"
                  >
                    {resetPwBusy ? 'Resetting…' : 'Reset'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Reset for customer delivery - the burn-in week wiped from the box
              and from the cloud. Developer only, like the row below it. */}
          {canSeeDevicePassword && (
            <div className="flex items-center gap-3">
              <CpuChipIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Reset device for customer delivery</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-700">
                    Wipes burn-in history and settings on the box and on the cloud. Ownership and DM Mobile access stay.
                  </span>
                  <button
                    onClick={openResetDialog}
                    disabled={!device.isOnline || customerReset?.status === 'pending'}
                    title={!device.isOnline ? 'Device is offline. It can only be reset while it is online.' : ''}
                    className="text-sm px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {customerReset?.status === 'pending' ? 'Resetting…' : 'Reset'}
                  </button>
                </div>
                {customerReset?.status === 'pending' && (
                  <p className="text-xs text-amber-600 mt-1">Resetting, waiting for the box to confirm</p>
                )}
                {customerReset?.status === 'done' && customerReset.completedAt && (
                  <p className="text-xs text-gray-500 mt-1">
                    Reset done {format(new Date(customerReset.completedAt), 'MMM d, yyyy HH:mm')} by {customerReset.requestedBy}
                  </p>
                )}
                {customerReset?.status === 'failed' && (
                  <p className="text-xs text-red-600 mt-1">
                    Last reset failed{customerReset.completedAt ? ` at ${format(new Date(customerReset.completedAt), 'MMM d, HH:mm')}` : ''}: {customerReset.error}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Device SSH password - assigned per box from here, applied on the
              box by the agent. Developer only, like the SSH access itself. */}
          {canSeeDevicePassword && (
            <div className="flex items-center gap-3">
              <CommandLineIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Device Password (SSH, user {is510 ? 'root' : 'dmi'})</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {!devicePw || devicePw.status === 'not_set' ? (
                    <span className="text-sm text-amber-600">Not set — box has the image default</span>
                  ) : (
                    <>
                      <span className="font-mono font-medium text-gray-900">
                        {showDevicePw
                          ? (devicePw.pendingPassword || devicePw.password)
                          : '••••••••••••'}
                      </span>
                      <button
                        onClick={() => setShowDevicePw(!showDevicePw)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                      >
                        {showDevicePw ? (
                          <EyeSlashIcon className="w-4 h-4 text-gray-600" />
                        ) : (
                          <EyeIcon className="w-4 h-4 text-gray-600" />
                        )}
                      </button>
                      {devicePw.status === 'pending' ? (
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Pending — awaiting box confirmation</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">Active</span>
                      )}
                    </>
                  )}
                  <button
                    onClick={handleAssignDevicePassword}
                    disabled={devicePwBusy || !device.isOnline}
                    title={!device.isOnline ? 'Device is offline — the password can only be changed while it is online' : ''}
                    className="text-sm px-2 py-1 bg-gray-700 text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    {devicePwBusy ? 'Sending…' : !devicePw || devicePw.status === 'not_set' ? 'Assign' : 'Rotate'}
                  </button>
                </div>
                {devicePwMessage && (
                  <p className="text-xs text-gray-500 mt-1">{devicePwMessage}</p>
                )}
              </div>
            </div>
          )}

          {/* Live Connection Status */}
          <div className="flex items-center gap-3">
            <SignalIcon className={`w-5 h-5 ${device.isOnline ? 'text-green-500' : 'text-gray-400'}`} />
            <div>
              <p className="text-sm text-gray-600">Connection Status</p>
              <div className="flex items-center gap-3">
                {device.isOnline ? (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <span className="w-2.5 h-2.5 bg-green-500 rounded-full block"></span>
                        <span className="w-2.5 h-2.5 bg-green-500 rounded-full absolute top-0 left-0 animate-ping"></span>
                      </div>
                      <span className="font-medium text-green-700">Online</span>
                      <span className="text-sm text-gray-500">
                        • {device.connectedAt ? formatDistanceToNow(new Date(device.connectedAt), { addSuffix: false }) : 'N/A'}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowConnectionModal(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                    >
                      View Details →
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 bg-gray-400 rounded-full block"></span>
                    <span className="font-medium text-gray-500">Offline</span>
                    {device.lastSeen && (
                      <span className="text-sm text-gray-400">
                        • Last seen {formatDistanceToNow(new Date(device.lastSeen), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Hardware Serial Numbers - QC set, developer only can modify */}
          <div className={`flex items-start gap-3 col-span-1 md:col-span-2 lg:col-span-3 border-t pt-4 mt-2 rounded-lg transition-all ${
            (!device.hardwareSerial || !device.smartBoardSerial)
              ? 'border-amber-200 bg-gradient-to-r from-amber-50/60 to-orange-50/40 -mx-2 px-4 pb-4'
              : 'border-gray-100'
          }`}>
            <div className="relative">
              <CpuChipIcon className={`w-5 h-5 mt-1 ${
                (!device.hardwareSerial || !device.smartBoardSerial) ? 'text-amber-500' : 'text-gray-400'
              }`} />
              {(!device.hardwareSerial || !device.smartBoardSerial) && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full animate-pulse"></span>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm text-gray-600 font-medium">Hardware Serial Numbers</p>
                {(!device.hardwareSerial || !device.smartBoardSerial) && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-100 rounded-full">
                    <svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Needs Setup
                  </span>
                )}
              </div>

              {!editingSerials ? (
                <div className="flex flex-wrap gap-6">
                  {/* Hardware Serial */}
                  <div className="min-w-[200px]">
                    <p className="text-xs text-gray-500 mb-1">Cabinet Serial Number</p>
                    <div className="flex items-center gap-2">
                      {device.hardwareSerial ? (
                        <span className="font-mono font-medium text-gray-900">{device.hardwareSerial}</span>
                      ) : (
                        <span className="text-amber-600 italic text-sm flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse"></span>
                          Not set
                        </span>
                      )}
                    </div>
                  </div>

                  {/* SmartBoard Serial */}
                  <div className="min-w-[200px]">
                    <p className="text-xs text-gray-500 mb-1">Smart Board Serial Number</p>
                    <div className="flex items-center gap-2">
                      {device.smartBoardSerial ? (
                        <span className="font-mono font-medium text-gray-900">{device.smartBoardSerial}</span>
                      ) : (
                        <span className="text-amber-600 italic text-sm flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse"></span>
                          Not set
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Edit button or restriction message */}
                  <div className="flex items-end">
                    {canEditSerials() ? (
                      <button
                        onClick={handleSerialsEdit}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1 ${
                          (!device.hardwareSerial || !device.smartBoardSerial)
                            ? 'text-white bg-amber-500 hover:bg-amber-600 shadow-sm'
                            : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                        }`}
                      >
                        <PencilIcon className="w-4 h-4" />
                        {(!device.hardwareSerial && !device.smartBoardSerial) ? 'Set Serial Numbers' : 'Edit'}
                      </button>
                    ) : (
                      <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                        Contact a developer to change
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4 max-w-md">
                  {serialsError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                      {serialsError}
                    </div>
                  )}

                  {/* Hardware Serial Input */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Cabinet Serial Number <span className="text-gray-400">e.g., i25225005</span>
                    </label>
                    <input
                      ref={hardwareSerialInputRef}
                      type="text"
                      value={hardwareSerialValue}
                      onChange={(e) => setHardwareSerialValue(e.target.value)}
                      placeholder="Enter cabinet serial number..."
                      disabled={device.hardwareSerial && !isDeveloper}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono ${
                        device.hardwareSerial && !isDeveloper
                          ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
                          : 'border-gray-300'
                      }`}
                    />
                    {device.hardwareSerial && !isDeveloper && (
                      <p className="text-xs text-amber-600 mt-1">Already set - Contact a developer to change</p>
                    )}
                  </div>

                  {/* SmartBoard Serial Input */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Smart Board Serial Number <span className="text-gray-400">e.g., sb23123383</span>
                    </label>
                    <input
                      type="text"
                      value={smartBoardSerialValue}
                      onChange={(e) => setSmartBoardSerialValue(e.target.value)}
                      placeholder="Enter smart board serial number..."
                      disabled={device.smartBoardSerial && !isDeveloper}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono ${
                        device.smartBoardSerial && !isDeveloper
                          ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
                          : 'border-gray-300'
                      }`}
                    />
                    {device.smartBoardSerial && !isDeveloper && (
                      <p className="text-xs text-amber-600 mt-1">Already set - Contact a developer to change</p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={handleSerialsSave}
                      disabled={savingSerials}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {savingSerials ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Saving...
                        </>
                      ) : (
                        'Save'
                      )}
                    </button>
                    <button
                      onClick={handleSerialsCancel}
                      disabled={savingSerials}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Live Connection Details Modal */}
      <AnimatePresence>
        {showConnectionModal && device.isOnline && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowConnectionModal(false)}
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <span className="w-3 h-3 bg-green-500 rounded-full block"></span>
                    <span className="w-3 h-3 bg-green-500 rounded-full absolute top-0 left-0 animate-ping"></span>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Live Connection Details</h2>
                    <p className="text-sm text-green-600">
                      Connected {device.connectedAt ? formatDistanceToNow(new Date(device.connectedAt), { addSuffix: true }) : 'recently'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowConnectionModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XCircleIcon className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              {/* Modal Body - Scrollable */}
              <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 120px)' }}>
                {/* Connection Status Header */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 mb-4 border border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <span className="w-3 h-3 bg-green-500 rounded-full block"></span>
                  <span className="w-3 h-3 bg-green-500 rounded-full absolute top-0 left-0 animate-ping"></span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-green-800">Device is Online</p>
                  <p className="text-xs text-green-600">
                    Connected {device.connectedAt ? formatDistanceToNow(new Date(device.connectedAt), { addSuffix: true }) : 'recently'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Session Duration</p>
                <p className="text-sm font-medium text-gray-900">
                  {device.connectedAt ? formatDistanceToNow(new Date(device.connectedAt)) : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Connection Details Grid */}
          <div className="space-y-4">
            {/* Technical Details */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <ServerIcon className="w-4 h-4 text-gray-600" />
                Connection Information
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-600">Session ID</p>
                  <p className="font-mono text-xs text-gray-900 break-all">
                    {device.sessionId || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">IP Address</p>
                  <p className="text-sm font-medium text-gray-900">{device.ip || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Connection Type</p>
                  <p className="text-sm font-medium text-gray-900">WebSocket</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Protocol Version</p>
                  <p className="text-sm font-medium text-gray-900">{device.protocolVersion || '1.0'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Last Heartbeat</p>
                  <p className="text-sm font-medium text-gray-900">
                    {device.lastHeartbeat ? 
                      formatDistanceToNow(new Date(device.lastHeartbeat), { addSuffix: true }) : 
                      'N/A'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Latency</p>
                  <p className="text-sm font-medium text-gray-900">
                    {device.latency ? `${device.latency}ms` : 'Measuring...'}
                  </p>
                </div>
              </div>
            </div>

            {/* Active Subscriptions */}
            {device.subscriptions && device.subscriptions.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <CloudArrowUpIcon className="w-4 h-4 text-gray-600" />
                  Active Data Streams
                </h4>
                <div className="space-y-2">
                  {device.subscriptions.map((sub, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 px-3 bg-white rounded border border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-gray-900">{sub.topic}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {sub.subscribedAt ? formatDistanceToNow(new Date(sub.subscribedAt), { addSuffix: true }) : 'Active'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Connection Statistics */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <ChartBarIcon className="w-4 h-4 text-gray-600" />
                Connection Statistics
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {device.messagesReceived || 0}
                  </p>
                  <p className="text-xs text-gray-600">Messages Received</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {device.messagesSent || 0}
                  </p>
                  <p className="text-xs text-gray-600">Messages Sent</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {device.dataTransferred ? `${(device.dataTransferred / 1024).toFixed(1)}` : '0'}
                  </p>
                  <p className="text-xs text-gray-600">Data (KB)</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {device.uptime || '100'}%
                  </p>
                  <p className="text-xs text-gray-600">Uptime</p>
                </div>
              </div>
            </div>

            {/* Socket Connections - Similar to User Page */}
            {device.socketConnections && device.socketConnections.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <WifiIcon className="w-4 h-4 text-gray-600" />
                  Active Socket Connections ({device.socketConnections.length})
                </h4>
                {device.socketConnections.map((socket, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-sm font-semibold text-gray-900">
                        Socket Connection #{idx + 1}
                      </h5>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        socket.readyState === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {socket.readyState === 1 ? 'OPEN' : 'CLOSED'}
                      </span>
                    </div>

                    {/* Connection State */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <p className="text-xs text-gray-600">Ready State</p>
                        <p className={`text-sm font-medium ${
                          socket.readyState === 1 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {socket.readyState === 0 ? 'CONNECTING' :
                           socket.readyState === 1 ? 'OPEN' :
                           socket.readyState === 2 ? 'CLOSING' :
                           socket.readyState === 3 ? 'CLOSED' : 'UNKNOWN'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Is Alive</p>
                        <p className={`text-sm font-medium ${
                          socket.isAlive ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {socket.isAlive ? 'Yes' : 'No'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Buffered Amount</p>
                        <p className="text-sm font-medium text-gray-900">{socket.bufferedAmount || 0} bytes</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Remote Address</p>
                        <p className="text-sm font-medium text-gray-900">{socket.remoteAddress || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Connected At</p>
                        <p className="text-sm font-medium text-gray-900">
                          {socket.connectedAt ? format(new Date(socket.connectedAt), 'M/d/yyyy, h:mm:ss a') : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Duration</p>
                        <p className="text-sm font-medium text-gray-900">
                          {socket.connectedAt ? formatDistanceToNow(new Date(socket.connectedAt)) : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* User Info from Token */}
                    {socket.userInfo && (
                      <div className="bg-blue-50 rounded-lg p-3 mb-3">
                        <p className="text-xs font-semibold text-blue-900 mb-2">User Info from Token</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-600">User ID:</span>
                            <span className="font-mono text-gray-900 ml-1">{socket.userInfo.userId}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Name:</span>
                            <span className="text-gray-900 ml-1">{socket.userInfo.name}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Email:</span>
                            <button
                              onClick={() => navigate(`/dashboard/users/${socket.userInfo.userId}`)}
                              className="text-blue-600 hover:text-blue-800 underline ml-1"
                            >
                              {socket.userInfo.email}
                            </button>
                          </div>
                          <div>
                            <span className="text-gray-600">Role:</span>
                            <span className="text-gray-900 ml-1">{socket.userInfo.role}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Subscriber Info */}
                    {socket.subscriberInfo && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-gray-900 mb-2">Subscriber Info</p>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Serial:</span>
                            <span className="font-mono text-gray-900">{socket.subscriberInfo.serial}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Topics:</span>
                            <span className="text-gray-900">{socket.subscriberInfo.topics?.join(', ') || 'None'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Subscribed At:</span>
                            <span className="text-gray-900">
                              {socket.subscriberInfo.subscribedAt ? 
                                format(new Date(socket.subscriberInfo.subscribedAt), 'h:mm:ss a') : 
                                'N/A'
                              }
                            </span>
                          </div>
                        </div>

                        {/* Active Subscriptions */}
                        {socket.subscriberInfo.activeSubscriptions && socket.subscriberInfo.activeSubscriptions.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-semibold text-gray-900 mb-1">
                              Active Subscriptions ({socket.subscriberInfo.activeSubscriptions.length})
                            </p>
                            <div className="space-y-1">
                              {socket.subscriberInfo.activeSubscriptions.map((sub, subIdx) => (
                                <div key={subIdx} className="flex items-center gap-2 text-xs bg-white p-2 rounded border border-gray-100">
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  <span className="font-medium text-gray-900">{sub.topic}</span>
                                  {sub.device && (
                                    <span className="text-gray-500">Device: {sub.device}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Active Users Summary */}
            {device.activeUsers && device.activeUsers.length > 0 && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <UserIcon className="w-4 h-4 text-blue-600" />
                  Users Monitoring This Device
                </h4>
                <div className="flex flex-wrap gap-2">
                  {device.activeUsers.map((user, idx) => (
                    <button
                      key={idx}
                      onClick={() => navigate(`/dashboard/users/${user.userId || user._id}`)}
                      className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-blue-200 hover:bg-blue-50 transition-colors"
                    >
                      <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-blue-700">
                          {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                        </span>
                      </div>
                      <span className="text-sm text-blue-600 hover:text-blue-800">{user.email || user.name}</span>
                      {user.role && (
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                          {user.role}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  )}
</AnimatePresence>

      {/* Live device viewers */}
      <CollapsibleSection
        title="Live Device Views"
        icon={CloudArrowUpIcon}
        defaultOpen={true}
      >
        <DeviceSubscriptions
          deviceSerial={device.serial}
          isOnline={device.isOnline}
          model={device.model}
        />
      </CollapsibleSection>

      {/* History Section */}
      <CollapsibleSection
        title="History & Data Sync"
        icon={ChartBarIcon}
        defaultOpen={false}
      >
        <DeviceHistory deviceId={device.serial} />
      </CollapsibleSection>

      {/* Beta 2 Model History Section - the DM510 panel keeps its model to itself */}
      {!is510 && (
      <CollapsibleSection
        title="Beta 2 History"
        icon={ChartBarIcon}
        defaultOpen={false}
      >
        <Beta2History deviceId={device.serial} />
      </CollapsibleSection>
      )}

      {/* Calibration History Section - a DM510 calibrates on its own keypad, nothing reaches the box */}
      {!is510 && (
      <CollapsibleSection
        title="Calibration History"
        icon={BeakerIcon}
        defaultOpen={false}
      >
        <CalibrationHistory deviceId={device.serial} />
      </CollapsibleSection>
      )}

      {/* Alarm History Section */}
      <CollapsibleSection
        title="Alarm History"
        icon={BellAlertIcon}
        defaultOpen={false}
      >
        <AlarmHistory deviceSerial={device.serial} />
      </CollapsibleSection>

      {/* Session Activity & Lifeline Section */}
      <CollapsibleSection
        title="Session Activity & Tracking"
        icon={ChartBarIcon}
        defaultOpen={false}
      >
        <SessionActivity deviceId={device.serial} />
      </CollapsibleSection>

      {/* Software Section - developer only, same gate as the /v1/software routes */}
      {isDeveloper && (
        <CollapsibleSection
          title="Software & Updates"
          icon={CloudArrowUpIcon}
          defaultOpen={false}
        >
          <DeviceSoftwareManager deviceId={device.serial} />
        </CollapsibleSection>
      )}

      {/* Database Health & Metrics Section - developer only; a DM510 box has no Postgres to report on */}
      {isDeveloper && !is510 && (
        <CollapsibleSection
          title="Database Health & Metrics"
          icon={CircleStackIcon}
          defaultOpen={false}
        >
          <DatabaseHealthManager deviceId={device.serial} />
        </CollapsibleSection>
      )}

      {/* Terminal Modal */}
      {showTerminal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <DeviceTerminal
              deviceSerial={device.serial}
              accessMode={showTerminal}
              onClose={() => setShowTerminal(false)}
            />
          </div>
        </div>
      )}

      {/* Reboot Confirmation Dialog */}
      {showResetDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <CpuChipIcon className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Reset device for customer delivery</h3>
                <p className="text-sm text-gray-600">{device.serial}</p>
              </div>
            </div>
            {resetSent ? (
              <div className="mb-2">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 text-sm">
                    Reset sent to the box. The row on this page shows when the box confirms, and the
                    cloud copy is purged at that moment.
                  </p>
                </div>
                <button
                  onClick={() => setShowResetDialog(false)}
                  className="mt-4 w-full px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  {is510 ? (
                    <>
                      <p className="text-gray-700 text-sm mb-3">
                        On the box: the stored history and the alarm bookkeeping are cleared; the DM510
                        panel itself keeps its settings, products and calibration, which this cannot touch.
                        On the cloud: this device&apos;s history, alarms, session logs and sync state are
                        deleted once the box confirms.
                      </p>
                      <p className="text-gray-700 text-sm mb-3">
                        Kept: the serial and registration, cloud credentials, network settings, the
                        software version, owners, operators and DM Mobile access.
                      </p>
                    </>
                  ) : (
                    <>
                  <p className="text-gray-700 text-sm mb-3">
                    On the box: every setting back to factory, products back to the factory list,
                    history, calibration, beta 2, bins and alarm history cleared, then the controller
                    and the screen restart. On the cloud: this device&apos;s history, beta 2, calibration,
                    alarms, session logs and sync state are deleted once the box confirms.
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    Kept: the serial and registration, cloud credentials, the SSH password, network
                    settings, the software version, owners, operators and DM Mobile access.
                  </p>
                    </>
                  )}
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                    <p className="text-red-800 text-sm font-medium">
                      This cannot be undone. Type the serial to confirm.
                    </p>
                  </div>
                  <label className="block text-sm text-gray-600 mb-1">Serial</label>
                  <input
                    type="text"
                    value={resetConfirmSerial}
                    onChange={(e) => setResetConfirmSerial(e.target.value.trim())}
                    placeholder={device.serial}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                  {resetError && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-red-700 text-sm">{resetError}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowResetDialog(false)}
                    disabled={resetBusy}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCustomerReset}
                    disabled={resetBusy || resetConfirmSerial !== device.serial}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {resetBusy ? 'Sending…' : 'Reset'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showHardwareDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <CpuChipIcon className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Hardware Replaced</h3>
                <p className="text-sm text-gray-600">Keeps serial: {device.serial}</p>
              </div>
            </div>

            {hwSuccess ? (
              <div className="mb-2">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 text-sm">{hwSuccess}</p>
                </div>
                <button
                  onClick={() => setShowHardwareDialog(false)}
                  className="mt-4 w-full px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-gray-700 text-sm mb-3">
                    The replacement unit takes over this device's identity: serial, owners,
                    history and alarms all stay. The replacement's temporary registration
                    is deleted and it reconnects as {device.serial} within a minute or two.
                  </p>
                  {device.isOnline && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                      <p className="text-amber-800 text-sm font-medium">
                        This device is currently ONLINE. Only proceed if the old unit is
                        being decommissioned — until it is unplugged, both units answer
                        to this serial.
                      </p>
                    </div>
                  )}
                  <label className="block text-sm text-gray-600 mb-1">Replacement unit (its temporary serial)</label>
                  <select
                    value={hwSourceSerial}
                    onChange={(e) => setHwSourceSerial(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Select the replacement unit…</option>
                    {hwCandidates.map((d) => (
                      <option key={d.serial} value={d.serial}>
                        {d.serial} — {d.isOnline ? 'online' : 'offline'} — registered {d.registeredAt ? format(new Date(d.registeredAt), 'MMM d, yyyy') : 'unknown'}
                      </option>
                    ))}
                  </select>
                  {hwError && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-red-700 text-sm">{hwError}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowHardwareDialog(false)}
                    disabled={hwBusy}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleHardwareReplaced}
                    disabled={hwBusy || !hwSourceSerial}
                    className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {hwBusy ? 'Transferring…' : 'Transfer Identity'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showRebootDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <ArrowPathIcon className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Device Reboot</h3>
                <p className="text-sm text-gray-600">Device: {device.serial}</p>
              </div>
            </div>
            
            {rebootCountdown !== null ? (
              <div className="mb-6">
                <p className="text-gray-700 mb-2">Reboot command sent successfully!</p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 font-medium">
                    Device will reboot in {rebootCountdown} seconds...
                  </p>
                  <div className="mt-2 bg-yellow-200 h-2 rounded-full overflow-hidden">
                    <div 
                      className="bg-yellow-600 h-full transition-all duration-1000"
                      style={{ width: `${(5 - rebootCountdown) * 20}%` }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-red-800 font-medium mb-2">⚠️ Warning</p>
                    <p className="text-red-700 text-sm">
                      This will immediately reboot the device. All active connections will be terminated 
                      and the device will be temporarily unavailable.
                    </p>
                  </div>
                  
                  <p className="text-gray-700">
                    Are you absolutely sure you want to reboot this device?
                  </p>
                  
                  {rebootError && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-red-700 text-sm">{rebootError}</p>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowRebootDialog(false);
                      setRebootError(null);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReboot}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                  >
                    Yes, Reboot Device
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default DeviceDetail;